import type { TFunction } from 'i18next';

import { hasValidCoordinates } from './coordinates';
import { NORWAY_BOUNDARY } from './norwayBoundary';

/** Inclusive bounding box for Norway including Svalbard (approx.). */
export const NORWAY_BBOX = {
  minLat: 57.0,
  maxLat: 81.5,
  minLon: 4.0,
  maxLon: 32.0
} as const;

/** Max distance when reference is a specific address point (matrikkel treff). */
export const MAX_DISTANCE_FROM_ADDRESS_REFERENCE_KM = 15;

/**
 * Maximum allowed distance when the reference point is the municipal center (no address geometry).
 * This value is larger than the address-reference maximum because municipal centers can be far from properties in large municipalities.
 */
export const MAX_DISTANCE_FROM_KOMMUNE_CENTER_KM = 75;

/**
 * Simplified Norway GeoJSON can sit inside the true land/water boundary. After an exact
 * point-in-polygon miss, we accept coordinates within this distance (km) of any ring edge
 * of the multipolygon (all pieces, outer and holes), in any direction.
 *
 * Kept intentionally generous: false positives (Sweden, sea) are acceptable; excluding real
 * Norway is not. ~18 km covers e.g. Utsira fyr vs this dataset.
 */
const NORWAY_BOUNDARY_TOLERANCE_KM = 18;

/**
 * When measuring distance from a point to the boundary, each polygon edge is a straight segment
 * in lon/lat; the closest point may lie between vertices. We sample that many equal steps along
 * each segment (steps + 1 positions) and take the minimum Haversine distance. More steps →
 * slightly better accuracy on long edges, more work when the tolerance path runs.
 */
const BOUNDARY_SEGMENT_SAMPLE_STEPS = 10;

const toRad = (deg: number) => (deg * Math.PI) / 180;

export const haversineDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return 2 * earthRadiusKm * Math.asin(Math.min(1, Math.sqrt(a)));
};

export const isInsideNorwayBbox = (latitude: number, longitude: number): boolean =>
  latitude >= NORWAY_BBOX.minLat &&
  latitude <= NORWAY_BBOX.maxLat &&
  longitude >= NORWAY_BBOX.minLon &&
  longitude <= NORWAY_BBOX.maxLon;

/**
 * Ray-casting point-in-polygon for a single linear ring. Ring coordinates are GeoJSON-order
 * `[longitude, latitude]`. The algorithm treats the ring as closed.
 */
const isPointInRing = (lat: number, lng: number, ring: number[][]): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersects =
      yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersects) {
      inside = !inside;
    }
  }
  return inside;
};

const isInsidePolygon = (latitude: number, longitude: number, polygon?: number[][][]): boolean => {
  const [outerRing, ...holes] = polygon ?? [];
  if (!outerRing) {
    return false;
  }

  if (!isPointInRing(latitude, longitude, outerRing)) {
    return false;
  }

  return !holes.some((hole) => isPointInRing(latitude, longitude, hole));
};

const minDistanceKmToNorwayBoundary = (latitude: number, longitude: number): number => {
  let minKm = Number.POSITIVE_INFINITY;
  const steps = BOUNDARY_SEGMENT_SAMPLE_STEPS;

  for (const polygon of NORWAY_BOUNDARY.coordinates) {
    for (const ring of polygon) {
      const n = ring.length;
      for (let k = 0; k < n; k++) {
        const [lon1, lat1] = ring[k]!;
        const [lon2, lat2] = ring[(k + 1) % n]!;
        for (let t = 0; t <= steps; t++) {
          const f = t / steps;
          const latS = lat1 + f * (lat2 - lat1);
          const lonS = lon1 + f * (lon2 - lon1);
          const d = haversineDistanceKm(latitude, longitude, latS, lonS);
          if (d < minKm) {
            minKm = d;
          }
        }
      }
    }
  }

  return minKm;
};

/**
 * Point-in-Norway check against the simplified Norway MultiPolygon (mainland + islands + Svalbard).
 * Exact ring hit first; otherwise within the configured boundary tolerance (km) of any boundary edge.
 */
export const isInsideNorwayBoundary = (latitude: number, longitude: number): boolean => {
  if (!isInsideNorwayBbox(latitude, longitude)) {
    return false;
  }

  for (const polygon of NORWAY_BOUNDARY.coordinates) {
    if (isInsidePolygon(latitude, longitude, polygon)) {
      return true;
    }
  }

  return minDistanceKmToNorwayBoundary(latitude, longitude) <= NORWAY_BOUNDARY_TOLERANCE_KM;
};

export type SolarCoordinateValidationContext = {
  referenceLatitude?: number;
  referenceLongitude?: number;
  /** True when we have a reference from address search or kommunesenter. */
  hasReferencePoint: boolean;
  /** True when reference coordinates are kommunesenter (not a matrikkel-adressepunkt). */
  referenceIsKommuneCenter: boolean;
};

/**
 * Hard validation: coordinates must fall inside Norway's boundary polygon. Submit is blocked
 * if this fails.
 */
export const validateSolarPanelCoordinates = (
  latitude: unknown,
  longitude: unknown,
  t: TFunction
): true | string => {
  if (!hasValidCoordinates(latitude, longitude)) {
    return true;
  }

  const lat = latitude as number;
  const lng = longitude as number;

  if (!isInsideNorwayBoundary(lat, lng)) {
    return t('register.solar.form.location.error.outsideNorway');
  }

  return true;
};

/**
 * Soft check: marker far from address/municipal center. Use only as a warning — do not block submission.
 */
export const getCoordinateDistanceWarningMessage = (
  latitude: unknown,
  longitude: unknown,
  ctx: SolarCoordinateValidationContext,
  t: TFunction
): string | null => {
  if (!hasValidCoordinates(latitude, longitude)) {
    return null;
  }

  const lat = latitude as number;
  const lng = longitude as number;

  if (!isInsideNorwayBoundary(lat, lng)) {
    return null;
  }

  if (!ctx.hasReferencePoint || !hasValidCoordinates(ctx.referenceLatitude, ctx.referenceLongitude)) {
    return null;
  }

  const maxKm = ctx.referenceIsKommuneCenter
    ? MAX_DISTANCE_FROM_KOMMUNE_CENTER_KM
    : MAX_DISTANCE_FROM_ADDRESS_REFERENCE_KM;
  const km = haversineDistanceKm(lat, lng, ctx.referenceLatitude as number, ctx.referenceLongitude as number);
  if (km <= maxKm) {
    return null;
  }

  return ctx.referenceIsKommuneCenter
    ? t('register.solar.form.location.warning.tooFarFromKommuneCenter', { maxKm })
    : t('register.solar.form.location.warning.tooFarFromAddress', { maxKm });
};
